import { css } from "@emotion/react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { Flex, Text } from "@phoenix/components";
import { isMirrorAnnotation } from "@phoenix/components/annotation/annotationUtils";
import { JSONText } from "@phoenix/components/code/JSONText";
import { Alert } from "@phoenix/components/core/alert";
import { Icons } from "@phoenix/components/core/icon";
import { Icon } from "@phoenix/components/core/icon/Icon";
import { Truncate } from "@phoenix/components/core/utility/Truncate";
import { PreformattedTextCell } from "@phoenix/components/table";
import { tableCSS } from "@phoenix/components/table/styles";
import { TableEmpty } from "@phoenix/components/table/TableEmpty";
import { TimestampCell } from "@phoenix/components/table/TimestampCell";
import { AnnotatorKindToken } from "@phoenix/components/trace/AnnotatorKindToken";
import { SpanAnnotationActionMenu } from "@phoenix/components/trace/SpanAnnotationActionMenu";
import { UserPicture } from "@phoenix/components/user/UserPicture";
import { useNotifySuccess } from "@phoenix/contexts";

import type {
  SpanFeedback_annotations$data,
  SpanFeedback_annotations$key,
} from "./__generated__/SpanFeedback_annotations.graphql";
import { SpanAnnotationsEmpty } from "./SpanAnnotationsEmpty";

type AnnotationTableRow = {
  id: string;
  name: string;
  label: string | null;
  score: number | null;
  explanation: string | null;
  metadata: unknown;
  annotatorKind: SpanFeedback_annotations$data["spanAnnotations"][number]["annotatorKind"];
  identifier: string;
  source: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  user: {
    username: string;
    profilePictureUrl?: string | null;
  } | null;
  spanNodeId: string;
  readOnly: boolean;
};

const spanFeedbackWrapCSS = css`
  overflow: auto;
`;

function AnnotationsTable({ rows }: { rows: AnnotationTableRow[] }) {
  const notifySuccess = useNotifySuccess();
  const [error, setError] = useState<string | null>(null);

  const columns: ColumnDef<AnnotationTableRow>[] = useMemo(
    () => [
      {
        header: "name",
        accessorKey: "name",
        size: 100,
      },
      {
        header: "annotator kind",
        accessorKey: "annotatorKind",
        size: 100,
        cell: ({ row }) => {
          const annotatorKind = row.original.annotatorKind;
          return <AnnotatorKindToken kind={annotatorKind} />;
        },
      },
      {
        header: "user",
        accessorKey: "user",
        size: 100,
        cell: ({ row }) => {
          const user = row.original.user;
          const userName = user?.username || "system";
          return (
            <Flex direction="row" gap="size-100" alignItems="center">
              <UserPicture
                name={userName}
                profilePictureUrl={user?.profilePictureUrl || null}
                size={18}
              />
              <span>{userName}</span>
            </Flex>
          );
        },
      },
      {
        header: "label",
        accessorKey: "label",
        size: 100,
      },
      {
        header: "score",
        accessorKey: "score",
        size: 100,
        cell: ({ row }) => {
          const score = row.original.score;
          return score != null ? `${score}%` : "--";
        },
      },
      {
        header: "explanation",
        accessorKey: "explanation",
        cell: PreformattedTextCell,
        size: 400,
      },
      {
        header: "source",
        accessorKey: "source",
        size: 100,
        cell: ({ row }) => row.original.source ?? "--",
      },
      {
        header: "identifier",
        accessorKey: "identifier",
        size: 100,
      },
      {
        header: "metadata",
        accessorKey: "metadata",
        minSize: 100,
        cell: ({ row }) => {
          const metadata = row.original.metadata;
          return metadata ? (
            <JSONText json={metadata} collapseSingleKey={false} />
          ) : (
            "--"
          );
        },
      },
      {
        header: "created at",
        accessorKey: "createdAt",
        size: 100,
        cell: TimestampCell,
      },
      {
        header: "updated at",
        accessorKey: "updatedAt",
        size: 100,
        cell: TimestampCell,
      },
      {
        header: "",
        accessorKey: "actions",
        size: 100,
        cell: ({ row }) => {
          if (row.original.readOnly) {
            return null;
          }
          return (
            <SpanAnnotationActionMenu
              annotationId={row.original.id}
              spanNodeId={row.original.spanNodeId}
              annotationName={row.original.name}
              onSpanAnnotationActionSuccess={notifySuccess}
              onSpanAnnotationActionError={(error) => {
                setError(error.message);
              }}
            />
          );
        },
      },
    ],
    [setError, notifySuccess]
  );

  const [sorting, setSorting] = useState<SortingState>([
    { id: "createdAt", desc: true },
  ]);
  // eslint-disable-next-line react-hooks-js/incompatible-library
  const table = useReactTable({
    columns,
    data: rows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });
  const isEmpty = table.getRowModel().rows.length === 0;

  return (
    <div css={spanFeedbackWrapCSS}>
      {error && <Alert variant="danger">{error}</Alert>}
      <table css={tableCSS} data-testid="span-annotations-table">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th colSpan={header.colSpan} key={header.id}>
                  {header.isPlaceholder ? null : (
                    <>
                      <div
                        {...{
                          className: header.column.getCanSort() ? "sort" : "",
                          onClick: header.column.getToggleSortingHandler(),
                          style: {
                            display: "flex",
                            alignItems: "center",
                            left: header.getStart(),
                            width: header.getSize(),
                          },
                        }}
                      >
                        <Truncate maxWidth="100%">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Truncate>
                        {header.column.getIsSorted() ? (
                          <Icon
                            className="sort-icon"
                            svg={
                              header.column.getIsSorted() === "asc" ? (
                                <Icons.ArrowUpFilled />
                              ) : (
                                <Icons.ArrowDownFilled />
                              )
                            }
                          />
                        ) : null}
                      </div>
                    </>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {isEmpty ? (
          <TableEmpty />
        ) : (
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      width: cell.column.getSize(),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  );
}

export function SpanFeedback({ span }: { span: SpanFeedback_annotations$key }) {
  "use no memo";
  const data = useFragment(
    graphql`
      fragment SpanFeedback_annotations on Span {
        id
        parentId
        spanAnnotations {
          id
          name
          label
          score
          explanation
          metadata
          annotatorKind
          identifier
          source
          createdAt
          updatedAt
          user {
            id
            username
            profilePictureUrl
          }
        }
        trace {
          id
          traceAnnotations {
            id
            name
            label
            score
            explanation
            metadata
            annotatorKind
            identifier
            source
            user {
              id
              username
              profilePictureUrl
            }
          }
        }
      }
    `,
    span
  );

  const spanNodeId = data.id;
  // Mirror annotations duplicate the canonical trace-level (run) verdict shown
  // here under "Run quality", so they are hidden from the span-level table.
  const spanAnnotations = data.spanAnnotations.filter(
    (annotation) => !isMirrorAnnotation(annotation)
  );
  // The root span represents the whole run, so its annotation list includes the
  // canonical trace-level (run) annotations. Child spans are observation-level
  // only.
  const traceAnnotations =
    data.parentId == null ? data.trace.traceAnnotations : [];

  const traceRows: AnnotationTableRow[] = traceAnnotations.map(
    (annotation) => ({
      id: annotation.id,
      name: annotation.name,
      label: annotation.label ?? null,
      score: annotation.score ?? null,
      explanation: annotation.explanation ?? null,
      metadata: annotation.metadata,
      annotatorKind: annotation.annotatorKind,
      identifier: annotation.identifier,
      source: annotation.source ?? null,
      createdAt: null,
      updatedAt: null,
      user: annotation.user
        ? {
            username: annotation.user.username,
            profilePictureUrl: annotation.user.profilePictureUrl,
          }
        : null,
      spanNodeId,
      readOnly: true,
    })
  );
  const spanRows: AnnotationTableRow[] = spanAnnotations.map((annotation) => ({
    id: annotation.id,
    name: annotation.name,
    label: annotation.label ?? null,
    score: annotation.score ?? null,
    explanation: annotation.explanation ?? null,
    metadata: annotation.metadata,
    annotatorKind: annotation.annotatorKind,
    identifier: annotation.identifier,
    source: annotation.source ?? null,
    createdAt: annotation.createdAt ?? null,
    updatedAt: annotation.updatedAt ?? null,
    user: annotation.user
      ? {
          username: annotation.user.username,
          profilePictureUrl: annotation.user.profilePictureUrl,
        }
      : null,
    spanNodeId,
    readOnly: false,
  }));

  if (traceRows.length === 0 && spanRows.length === 0) {
    return <SpanAnnotationsEmpty />;
  }

  const hasBothSections = traceRows.length > 0 && spanRows.length > 0;
  return (
    <Flex direction="column" gap="size-200" css={spanFeedbackWrapCSS}>
      {traceRows.length > 0 ? (
        <Flex direction="column" gap="size-100">
          <Text elementType="h3" size="S" color="text-700" css={headingCSS}>
            Run quality
          </Text>
          <AnnotationsTable rows={traceRows} />
        </Flex>
      ) : null}
      {spanRows.length > 0 ? (
        <Flex direction="column" gap="size-100">
          {hasBothSections ? (
            <Text elementType="h3" size="S" color="text-700" css={headingCSS}>
              Span annotations
            </Text>
          ) : null}
          <AnnotationsTable rows={spanRows} />
        </Flex>
      ) : null}
    </Flex>
  );
}

const headingCSS = css`
  padding: var(--ac-global-dimension-size-200) var(--ac-global-dimension-size-200) 0;
`;
